import { doc, getDoc, getDocs, query, collection, where, updateDoc } from 'firebase/firestore';
import { db, auth } from '../firebase.js';

/**
 * Migration script to fix existing parents who don't have assignedSchoolId
 * This script finds parents with assignedSchoolId: null and updates them based on their linked children
 */
async function migrateParentSchoolAssignments() {
  const user = auth.currentUser;
  if (!user) {
    console.error('❌ No authenticated user found');
    return;
  }

  console.log('🔄 Starting parent school assignment migration...');

  try {
    // Step 1: Find all parentChildLinks
    const parentLinksQuery = query(collection(db, 'parentChildLinks'));
    const parentLinksSnapshot = await getDocs(parentLinksQuery);
    
    console.log(`📊 Found ${parentLinksSnapshot.docs.length} parent-child links`);

    // Step 2: Group links by parentId and collect unique schoolIds
    const parentSchoolMap = new Map();
    
    for (const linkDoc of parentLinksSnapshot.docs) {
      const linkData = linkDoc.data();
      const parentId = linkData.parentId;
      const schoolId = linkData.schoolId;
      
      if (!parentSchoolMap.has(parentId)) {
        parentSchoolMap.set(parentId, new Set());
      }
      parentSchoolMap.get(parentId).add(schoolId);
    }

    console.log(`👥 Found ${parentSchoolMap.size} unique parents to process`);

    // Step 3: For each parent, check if they need assignedSchoolId and update them
    let updatedCount = 0;
    let skippedCount = 0;

    for (const [parentId, schoolIds] of parentSchoolMap) {
      try {
        const userDocRef = doc(db, 'users', parentId);
        const userDoc = await getDoc(userDocRef);
        
        if (!userDoc.exists()) {
          console.warn(`⚠️ User document not found for parent: ${parentId}`);
          skippedCount++;
          continue;
        }

        const userData = userDoc.data();
        
        // Skip if not a parent or already has assignedSchoolId
        if (!userData.parent || userData.assignedSchoolId) {
          skippedCount++;
          continue;
        }

        // Get the first schoolId (for parents with multiple children in different schools)
        // In practice, most parents will have children in one school
        const schoolId = Array.from(schoolIds)[0];
        
        console.log(`📝 Updating parent ${parentId} with schoolId: ${schoolId}`);
        
        await updateDoc(userDocRef, {
          assignedSchoolId: schoolId
        });

        updatedCount++;
        console.log(`✅ Updated parent ${parentId} -> assignedSchoolId: ${schoolId}`);

      } catch (error) {
        console.error(`❌ Failed to update parent ${parentId}:`, error.message);
        skippedCount++;
      }
    }

    console.log(`
🎉 Migration Complete!
- Updated: ${updatedCount} parents
- Skipped: ${skippedCount} parents (already had assignedSchoolId or not applicable)
- Total processed: ${parentSchoolMap.size} parents
    `);

  } catch (error) {
    console.error('❌ Migration failed:', error);
  }
}

/**
 * Verify a specific parent's data structure
 */
async function checkParentData(parentId) {
  try {
    const userDocRef = doc(db, 'users', parentId);
    const userDoc = await getDoc(userDocRef);
    
    if (!userDoc.exists()) {
      console.log(`❌ Parent ${parentId} not found`);
      return;
    }

    const userData = userDoc.data();
    console.log(`
👤 Parent Data for ${parentId}:
- parent: ${userData.parent}
- assignedSchoolId: ${userData.assignedSchoolId}
- email: ${userData.email}
    `);

    // Also check their linked children
    const linksQuery = query(collection(db, 'parentChildLinks'), where('parentId', '==', parentId));
    const linksSnapshot = await getDocs(linksQuery);
    
    console.log(`👶 Linked children: ${linksSnapshot.docs.length}`);
    linksSnapshot.docs.forEach(linkDoc => {
      const linkData = linkDoc.data();
      console.log(`  - Student: ${linkData.studentId}, School: ${linkData.schoolId}`);
    });

  } catch (error) {
    console.error('❌ Failed to check parent data:', error);
  }
}

export { migrateParentSchoolAssignments, checkParentData };